import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import * as React from 'react';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { ThunderboltFilled } from '@ant-design/icons';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat } from '@/timeHelpers';
import { flatMap, groupBy, sortBy } from 'lodash';
import { AppMonitoringHealthMetric, IntegrationType } from '@/types';
import { displayNameForIntegrationType } from '@/helpers/integrationHelpers';
import { unitForMonitoringMetric } from '@/helpers';
import { displayFormattedMonitoringMetricValue, displayTextForMonitoringMetric } from '@/helpers/displayHelpers';
import { symbolForMetricConditionOperator } from '@/typescriptHelpers';

export const lastUpdatedLabel = (timestamp: string | undefined) =>
    timestamp ? `Last updated ${dayjs.utc(timestamp).local().format(DatetimeFormat.ShortMonthDateTime)}` : undefined;

const METRIC_DATA_NOT_YET_AVAILABLE_TEXT = 'Data is not yet available';
const METRIC_DATA_NOT_YET_AVAILABLE_TOOLTIP =
    "This integration hasn't reported any data for this release yet. Some metrics, like Apple power and performance metrics, can take a few days to become available.";

export const MetricDataNotYetAvailableText: React.FC<{ fontSize: number; lineHeight?: string }> = ({
    fontSize,
    lineHeight,
}) => (
    <Tooltip title={METRIC_DATA_NOT_YET_AVAILABLE_TOOLTIP}>
        <Typography.Text
            ellipsis
            style={{ color: antGrey.grey6, fontSize, lineHeight, whiteSpace: 'nowrap', minWidth: 0 }}
        >
            {METRIC_DATA_NOT_YET_AVAILABLE_TEXT}
        </Typography.Text>
    </Tooltip>
);

export const renamedMetricTooltip = (
    customDisplayName: string | undefined,
    generatedTitle: string,
    isDisplayNameTruncated = false,
) =>
    customDisplayName ?
        isDisplayNameTruncated ? `${customDisplayName} (${generatedTitle})`
        :   generatedTitle
    :   undefined;

// e.g. "Session adoption is >= 2% (Custom)"
export const displayTextForHealthMetricPrecondition = (metric: AppMonitoringHealthMetric): string => {
    const threshold = metric.thresholds[0];
    const comparison =
        threshold ?
            ` is ${symbolForMetricConditionOperator(threshold.condition)} ${displayFormattedMonitoringMetricValue(
                metric.type,
                threshold.value,
                unitForMonitoringMetric(metric),
            )}`
        :   '';
    return `${displayTextForMonitoringMetric(metric)}${comparison} (${displayNameForIntegrationType(metric.integrationId)})`;
};

export const sortMonitoringMetricsForDisplay = (metrics: AppMonitoringHealthMetric[]): AppMonitoringHealthMetric[] => {
    const integrationGroupsByName = sortBy(
        Object.entries(groupBy(metrics, (metric) => metric.integrationId)),
        ([integrationId]) => displayNameForIntegrationType(integrationId as IntegrationType),
    );
    return flatMap(integrationGroupsByName, ([, metricsForIntegration]) =>
        metricsForIntegration.sort((a, b) => {
            if (a.eventName && b.eventName) {
                if (a.eventName === b.eventName) {
                    return a.type < b.type ? -1 : 1;
                }

                return a.eventName < b.eventName ? -1 : 1;
            } else {
                return a.type < b.type ? -1 : 1;
            }
        }),
    );
};

export const generateAutomationThunderboltIcon: (args: {
    isUsedForAutoAccelerate: boolean;
    isUsedForAutoHalt: boolean;
    prefix?: string;
}) => React.ReactNode = ({ isUsedForAutoAccelerate, isUsedForAutoHalt, prefix }) => {
    return (
        (isUsedForAutoAccelerate || isUsedForAutoHalt) && (
            <Tooltip
                title={() => {
                    if (isUsedForAutoAccelerate && isUsedForAutoHalt) {
                        return (
                            <Typography.Text
                                style={{
                                    color: 'white',
                                }}
                            >
                                {prefix} <strong>Release stable phased releases to 100% of users</strong> and{' '}
                                <strong>the Halt unstable phased releases</strong> automations.
                            </Typography.Text>
                        );
                    } else if (isUsedForAutoAccelerate) {
                        return (
                            <Typography.Text
                                style={{
                                    color: 'white',
                                }}
                            >
                                {prefix} <strong>Release stable phased releases to 100% of users</strong> automation.
                            </Typography.Text>
                        );
                    } else if (isUsedForAutoHalt) {
                        return (
                            <Typography.Text
                                style={{
                                    color: 'white',
                                }}
                            >
                                {prefix} <strong>Halt unstable phased releases</strong> automation.
                            </Typography.Text>
                        );
                    }

                    return null;
                }}
            >
                <ThunderboltFilled style={{ color: runwayColors.primary }} />
            </Tooltip>
        )
    );
};
